import {NavigationTab} from '@/logged/features';
import {useNotifications} from '@/logged/features/notifications';
import {useUser} from '@/logged/features/user';
import {I18nService} from '@malt/vue3-i18n';
import {computed, Ref} from 'vue';
import {CachedFilteredTab, useFilteredTabs} from '../useFilteredTabs';
import {NavigationTabsRecord} from './navigation.types';
import {storeToRefs} from 'pinia';
import {useProfilePreferences} from '@/logged/features/aside-menu/composables/useProfilePreferences';
import {track} from '@malt/tracking';
import {useNavbarState} from '@/logged/features/aside-menu/stores/navbar-state.store';

export function useFreelancerNavigationTabs(cachedNavigationRoutes: Ref<CachedFilteredTab[]>): NavigationTabsRecord {
    const userStore = useUser();
    const isPlatformAdmin = computed<boolean>(() => !!userStore.user?.options.platformAdmin);
    const notificationsStore = useNotifications();

    const currentLanguage = computed(() => userStore.user?.currentLanguage);
    const currentCountry = computed(() => userStore.user?.currentCountry);

    const logoNavigationRoute = computed(() => '/dashboard/freelancer');

    const preferenceStore = useProfilePreferences();
    const {preferences} = storeToRefs(preferenceStore);

    const navbarStore = useNavbarState();
    const {state} = storeToRefs(navbarStore);

    const freelancerOnboarded = computed(() => userStore.user?.options.onboarded);
    const isBusinessReferralActivated = computed(() => !!userStore.user?.featureFlags['freelancer-business-referral']);

    const resourcesUrls: {[index: string]: string} = {
        FR: '/resources',
        'en-FR': 'https://www.malt.fr/resources',
        GB: '/resources',
        BE: '/resources',
        'BE-en': 'https://fr.malt.be/c/resources',
        DE: '/resources',
        'DE-en': 'https://www.malt.de/resources',
        ES: '/resources',
        'ES-en': 'https://www.malt.es/resources',
        NL: '/resources',
        'NL-en': 'https://www.malt.nl/resources',
        'CH-fr': 'https://www.malt.fr/resources',
    };
    const resourceUrl = computed(() => {
        if (currentLanguage && currentCountry && resourcesUrls[currentLanguage.value + '-' + currentCountry.value]) {
            return resourcesUrls[currentLanguage.value + '-' + currentCountry.value];
        }
        if (currentCountry && resourcesUrls[currentCountry.value as string]) {
            return resourcesUrls[currentCountry.value as string];
        }
        return 'https://www.malt.uk/resources';
    });

    const createNavigationTab = computed(() =>
        isBusinessReferralActivated.value
            ? {
                  id: 'freelancer-create',
                  title: I18nService.msg('navbar.aside.tabs.title.freelancer.create'),
                  icons: {mainIcon: 'aside/plus', canBeActive: false},
                  border: true,
                  showChildrenInADropdown: true,
                  children: [
                      {
                          id: 'freelancer-create-a-quote',
                          title: I18nService.msg('navbar.aside.tabs.title.freelancer.quote'),
                          url: {href: '/proposal/draft'},
                          action: () => track('quote_started', {source: 'navbar'}),
                      },
                      {
                          id: 'freelancer-create-a-business-referral',
                          title: I18nService.msg('navbar.aside.tabs.title.freelancer.business.referral'),
                          url: state.value.isBusinessReferralOnboardingAcknowledged
                              ? {href: I18nService.msg('business-referral.typeform.href'), blank: true}
                              : {href: '/business-referral'},
                      },
                  ],
              }
            : {
                  id: 'freelancer-create-a-quote',
                  title: I18nService.msg('navbar.aside.tabs.title.freelancer.create.a.quote'),
                  url: {href: '/proposal/draft'},
                  icons: {mainIcon: 'aside/plus', canBeActive: false},
                  border: true,
              },
    );

    const manageProjectsNavigationTab = computed(() =>
        isBusinessReferralActivated.value
            ? {
                  id: 'freelancer-manage-projects',
                  title: I18nService.msg('navbar.aside.tabs.title.freelancer.manage.projects'),
                  icons: {mainIcon: 'aside/manage-projects'},
                  children: [
                      {
                          id: 'freelancer-my-projects',
                          title: I18nService.msg('navbar.aside.tabs.title.freelancer.my.projects'),
                          url: {href: '/missions'},
                          isActiveWhen: ({startWith}: any) => startWith('/missions'),
                      },
                      {
                          id: 'freelancer-my-referrals',
                          title: I18nService.msg('navbar.aside.tabs.title.freelancer.my.referrals'),
                          url: {href: '/business-referral/my-referrals'},
                          isActiveWhen: ({startWith}: any) => startWith('/business-referral'),
                      },
                  ],
              }
            : {
                  id: 'freelancer-manage-projects',
                  title: I18nService.msg('navbar.aside.tabs.title.freelancer.manage.projects'),
                  url: {href: '/missions'},
                  icons: {mainIcon: 'aside/manage-projects'},
                  isActiveWhen: ({startWith}: any) => startWith('/missions'),
              },
    );

    const freelancerComplianceNotificationCounter = computed(() => {
        const domain = window.location.origin;
        const domainExtension = domain.split('.').pop()!.toLowerCase();

        if (['es', 'fr'].includes(domainExtension)) {
            return notificationsStore.notificationsRecord?.freelancerCompliance || 0;
        }

        return 0;
    });

    const navigationRoutes = computed((): NavigationTab[] => [
        createNavigationTab.value,
        {
            id: 'freelancer-dashboard',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.dashboard'),
            url: {href: '/dashboard/freelancer'},
            icons: {mainIcon: 'aside/dashboard'},
            isActiveWhen: ({startWith}) => startWith('/dashboard/freelancer'),
        },
        {
            id: 'freelancer-inbox',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.inbox'),
            icons: {mainIcon: 'aside/inbox'},
            url: {href: '/messages'},
            isActiveWhen: ({startWith}) => startWith('/messages'),
            notificationsCount: notificationsStore.notificationsRecord?.messages,
        },
        {
            id: 'freelancer-enhance-my-profile',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.enhance.my.profile'),
            icons: {mainIcon: 'aside/profile'},
            children: [
                {
                    id: 'freelancer-view-profile',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.view.profile'),
                    url: {href: '/profile'},
                    isActiveWhen: ({startWith}) => startWith('/profile'),
                },
                {
                    id: 'freelancer-stats',
                    constraint: preferences.value.searchVisibilityChoice,
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.stats'),
                    url: {href: '/analytics/views'},
                    isActiveWhen: ({startWith}) => startWith('/analytics/views'),
                },
                {
                    id: 'freelancer-project-preferences',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.project.preferences'),
                    url: {href: '/mission-preferences'},
                    isActiveWhen: ({startWith}) => startWith('/mission-preferences'),
                },
            ],
        },
        manageProjectsNavigationTab.value,
        {
            id: 'freelancer-invoices-payments',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.invoices.and.payments'),
            url: {href: '/freelancer/finance'},
            icons: {mainIcon: 'aside/invoice'},
            isActiveWhen: ({startWith}) => startWith('/freelancer/finance') || startWith('/company/payment_in') || startWith('/company-wallet'),
        },
        {
            separator: 'top',
            id: 'freelancer-company',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.company'),
            url: {href: '/company'},
            icons: {mainIcon: 'aside/company'},
            isActiveWhen: ({startWith}) => startWith('/freelancer/company'),
            notificationsCount: freelancerComplianceNotificationCounter.value,
        },
        {
            id: 'freelancer-resources',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.resources'),
            icons: {mainIcon: 'aside/resources'},
            children: [
                {
                    id: 'freelancer-resources-center',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.resources.center'),
                    url: {href: resourceUrl.value, blank: true},
                },
                {
                    id: 'freelancer-daily-rate-tracker',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.daily.rate.tracker'),
                    constraint: currentCountry.value === 'FR' && currentLanguage.value === 'fr',
                    url: {href: '/t/barometre-tarifs'},
                    isActiveWhen: ({startWith}) => startWith('/t/barometre-tarifs'),
                },
                {
                    id: 'freelancer-daily-rate-calculator',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.daily.rate.calculate'),
                    constraint: currentCountry.value === 'ES' && currentLanguage.value === 'es',
                    url: {href: 'https://calculadora.malt.es/', blank: true},
                },
                {
                    id: 'freelancer-partners',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.partners'),
                    url: {href: '/c/freelancers/partners', blank: true},
                },
                {
                    id: 'freelancer-help-center',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.help.center'),
                    url: {href: `https://help.malt.com/kb/${currentLanguage.value}/18216`, blank: true, heapId: `navbar-tab__freelance-help_center`},
                },
            ],
        },
        {
            id: 'freelancer-settings',
            title: I18nService.msg('navbar.aside.tabs.title.freelancer.settings'),
            icons: {mainIcon: 'aside/settings'},
            children: [
                {
                    id: 'freelancer-personal',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.personal'),
                    url: {href: '/account'},
                    isActiveWhen: ({startWith}) => startWith('/account'),
                },
                {
                    id: 'freelancer-admin',
                    title: I18nService.msg('navbar.aside.tabs.title.admin'),
                    url: {href: '/admin', blank: true},
                    constraint: isPlatformAdmin.value,
                    isActiveWhen: ({startWith}) => startWith('/admin'),
                },
                {
                    id: 'freelancer-logout',
                    title: I18nService.msg('navbar.aside.tabs.title.freelancer.logout'),
                    customHoverColor: `var(--joy-color-primary-10)`,
                    action: async () => {
                        await userStore.logoutUser();
                    },
                },
            ],
        },
    ]);

    const allNavigationRoutes = computed((): NavigationTab[] => {
        if (!freelancerOnboarded.value && window.location.pathname === '/dashboard/freelancer') {
            return navigationRoutes.value.concat([{id: 'navbar-availability', title: ''}]);
        }
        return navigationRoutes.value;
    });

    const {filteredNavigationRoutes} = useFilteredTabs(cachedNavigationRoutes, allNavigationRoutes);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
